import React, { useEffect, useRef, useState } from "react";
import InputAuto from "./InputAuto";
import { API } from "../api/api";

// Server-side search wrapper around InputAuto. Endpoint must accept `term` and
// `selectedId` query params and return { status, result: [{ id, label, subtitle? }] }.
// Debounces input at 300ms, caps at 20 rows per query.
const ServerSearchAutocomplete = ({
  value,
  onChange,
  endpoint,
  placeholder,
  label,
  name,
  size = "small",
  variant = "outlined",
  disableClearable = false,
  // Full width by default, like every other form control in this app. Without it
  // the Autocomplete sizes to its content, so a picker sitting next to a
  // fullWidth field collapsed to ~90px and clipped its own label.
  fullWidth = true,
  // Extra query params for endpoints that need scoping beyond term/selectedId —
  // e.g. CashSafe/SearchLabels wants companyId + outletId + forTill. Given as an
  // object so callers never hand-build a query string: they used to be forced to
  // append one to `endpoint`, which put their params AFTER the `&_=` guard below
  // and fed the trailing slash straight into the last value.
  //
  // null / undefined / "" entries are dropped, so `{ outletId }` on a screen with
  // no outlet yet simply omits the param rather than sending "undefined".
  params,
  // Anything else (error, disabled, sx, …) reaches InputAuto rather than being
  // silently dropped — this component used to be a dead end for props.
  ...rest
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  // Remember the user's pick so it stays renderable even after options get
  // swapped by a subsequent search fetch.
  const [selectedOption, setSelectedOption] = useState(null);
  const debounceRef = useRef(null);

  const fetchOptions = (term, selId) => {
    setLoading(true);
    // Trailing `_=` stays LAST and absorbs the slash that API() appends, so query
    // params don't get corrupted (e.g. `selectedId=39/` would fail int binding).
    // Everything else is pushed in front of it.
    const parts = [];
    if (term) parts.push(`term=${encodeURIComponent(term)}`);
    else if (selId) parts.push(`selectedId=${selId}`);

    Object.entries(params || {}).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") return;
      parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
    });

    parts.push("_=");
    const qs = `?${parts.join("&")}`;
    API(endpoint + qs).get((res) => {
      setLoading(false);
      if (res?.status === 200) {
        setOptions(res.result || []);
        return;
      }
      // A failing picker endpoint used to be indistinguishable from a business
      // with no records: the response was dropped and the dropdown just rendered
      // empty. That hid a 500 from an untranslatable query for a whole round of
      // debugging. Options are cleared (stale rows would be a lie) but the reason
      // is left somewhere findable.
      setOptions([]);
      // eslint-disable-next-line no-console
      console.error(
        `[ServerSearchAutocomplete] ${endpoint} returned ${res?.status ?? "no response"}`,
        res?.msg || ""
      );
    });
  };

  // Initial fetch — pass current value as selectedId so the label renders even
  // if the preselected id isn't in the default top-N set.
  useEffect(() => {
    fetchOptions("", value);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the parent clears the value, drop the sticky selection too.
  useEffect(() => {
    if (!value) setSelectedOption(null);
  }, [value]);

  const handleInput = (_e, term, reason) => {
    if (reason !== "input") return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchOptions(term || ""), 300);
  };

  // Merge the sticky selected option into the visible options so InputAuto's
  // `options.find(id === value)` always succeeds.
  const mergedOptions =
    selectedOption && !options.some((o) => o.id === selectedOption.id)
      ? [selectedOption, ...options]
      : options;

  return (
    <InputAuto
      name={name}
      value={value}
      options={mergedOptions}
      loading={loading}
      noFilter
      disableClearable={disableClearable}
      placeholder={placeholder}
      label={label}
      size={size}
      variant={variant}
      fullWidth={fullWidth}
      {...rest}
      onChange={(e, picked) => {
        setSelectedOption(picked || null);
        // Forward the full picked option so callers can read extras
        // (existing call sites that take only the id ignore the 2nd arg).
        onChange(e?.target?.value ?? "", picked || null);
      }}
      onInput={handleInput}
    />
  );
};

export default ServerSearchAutocomplete;
